import React, { useState, useEffect, useCallback, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import Typography from '@mui/material/Typography';
import { ELocalStorageKeys } from '../../constants/enums';
import { webStorageUtil } from '../../state/webStorage.state.service';
import useAnalytics from '../../hooks/useAnalytics';
import { useLoginState } from 'state/hooks/login.state.hook';

interface CookieSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  onSave?: (enableAnalytics: boolean) => void;
  variant?: 'modal' | 'inline';
}

const CookieSettingsDialog: React.FC<CookieSettingsDialogProps> = ({
  open,
  onClose,
  onSave,
  variant = 'inline',
}) => {
  const [enableAnalytics, setEnableAnalytics] = useState(true);
  const { t } = useTranslation();
  const { optOut, optIn } = useAnalytics();
  const { setShowCookieSettingsDialog } = useLoginState();

  useEffect(() => {
    if (open) {
      const currentAnalytics = webStorageUtil.get(ELocalStorageKeys.ANALYTICS);
      if (currentAnalytics !== null) {
        setEnableAnalytics(JSON.parse(currentAnalytics));
      }
      setShowCookieSettingsDialog(true);
    }
  }, [open]);

  const handleSaveSettings = useCallback(() => {
    webStorageUtil.set(ELocalStorageKeys.ANALYTICS, JSON.stringify(enableAnalytics));
    
    if (enableAnalytics) {
      optIn();
    } else {
      optOut();
    }
    
    if (onSave) {
      onSave(enableAnalytics);
    }
    onClose();
  }, [enableAnalytics, onSave, onClose, optIn, optOut]);

  const handleToggleAnalytics = () => {
    setEnableAnalytics((prev) => !prev);
  };

  const handleOverlayClick = useCallback(
    (event: MouseEvent) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleDialogClick = (event: MouseEvent) => {
    event.stopPropagation();
  };

  const DialogContent = (
    <>
      <Typography variant="h3" sx={{ color: 'black' }}>
        {t('cookieConsent.cookiesSettings')}
      </Typography>
      <div>
        <div className="header">
          <Typography variant="h4" fontWeight={500} sx={{ color: 'black' }}>
            {t('cookieConsent.strictlyNecessaryCookies.title')}
          </Typography>
          <label className="switch disabled">
            <input type="checkbox" checked />
            <span className="slider round"></span>
          </label>
        </div>
        <Typography sx={{ fontSize: '14px', color: 'black', lineHeight: '1.5' }} fontWeight={300}>
          {t('cookieConsent.strictlyNecessaryCookies.description')}
        </Typography>
      </div>
      <div>
        <div className="header">
          <Typography variant="h4" fontWeight={500} sx={{ color: 'black' }}>
            {t('cookieConsent.analyticsCookies.title')}
          </Typography>
          <label className="switch">
            <input type="checkbox" checked={enableAnalytics} onChange={handleToggleAnalytics} />
            <span className="slider round"></span>
          </label>
        </div>
        <Typography sx={{ fontSize: '14px', color: 'black', lineHeight: '1.5' }} fontWeight={300}>
          {t('cookieConsent.analyticsCookies.description')}
        </Typography>
      </div>
      <button className="save-settings" onClick={handleSaveSettings}>
        {t('cookieConsent.saveSettings')}
      </button>
    </>
  );

  if (!open) return null;

  if (variant === 'modal') {
    return (
      <div id="cookieConsent" onClick={handleOverlayClick}>
        <div className="dialog" onClick={handleDialogClick}>
          {DialogContent}
        </div>
      </div>
    );
  }

  return (
    <div className="dialog" onClick={handleDialogClick}>
      {DialogContent}
    </div>
  );
};

export default CookieSettingsDialog;
